import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Bomb, Wallet, AlertTriangle } from 'lucide-react';
import { DraftSabotageConfig } from '@/types/draftSabotageSettings';
import { cn } from '@/lib/utils';

interface DraftSabotagesPanelProps {
  walletBalance: number;
  teams: { id: string; name: string; owner: string }[];
  myTeamId: string;
  onApplySabotage: (sabotageId: string, targetTeamId: string, overrideCost?: number) => void | Promise<void>;
  appliedSabotageCount: number;
  maxSabotagesPerDraft: number;
  availableSabotages: DraftSabotageConfig[];
  hasBeenSabotaged?: boolean;
  sabotageHitCount?: number;
  /** Combined beta sabotage credits */
  draftCredits?: number;
  /** Current draft round — used to block pick skip before round 10 */
  currentRound?: number;
}

const colorClasses: Record<string, string> = {
  damage: 'border-damage/50 bg-damage/10 hover:bg-damage/20',
  primary: 'border-primary/50 bg-primary/10 hover:bg-primary/20',
  warning: 'border-warning/50 bg-warning/10 hover:bg-warning/20',
  success: 'border-success/50 bg-success/10 hover:bg-success/20',
};

const DraftSabotagesPanel = ({
  walletBalance,
  teams,
  myTeamId,
  onApplySabotage,
  appliedSabotageCount,
  maxSabotagesPerDraft,
  availableSabotages,
  hasBeenSabotaged = false,
  sabotageHitCount = 0,
  draftCredits = 0,
  currentRound = 1,
}: DraftSabotagesPanelProps) => {
  const [selectedSabotage, setSelectedSabotage] = useState<DraftSabotageConfig | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const otherTeams = teams.filter(t => t.id !== myTeamId);
  const sabotagesRemaining = maxSabotagesPerDraft - appliedSabotageCount;
  // Display the effective spendable balance — credits if available, otherwise wallet
  const displayBalance = draftCredits > 0 ? draftCredits : walletBalance;

  // Karma dynamic pricing: 4+ hits = $15, 2-3 = $20, 1 = $25
  const getKarmaCost = () => {
    if (sabotageHitCount >= 4) return 15;
    if (sabotageHitCount >= 2) return 20;
    return 25;
  };

  const isKarma = (s: DraftSabotageConfig) => s.name === 'Karma';
  const isGlobalSabotage = (s: DraftSabotageConfig) => s.name === 'Snake Reversal';

  const handleConfirmSabotage = async () => {
    if (!selectedSabotage || isApplying) return;
    const isKarmaSab = isKarma(selectedSabotage);
    const isGlobal = isGlobalSabotage(selectedSabotage);
    const targetId = (isKarmaSab || isGlobal) ? myTeamId : selectedTargetId;
    if (targetId) {
      setIsApplying(true);
      try {
        // Pass dynamic Karma cost so wallet deduction uses the correct amount
        const costOverride = isKarmaSab ? getKarmaCost() : undefined;
        await onApplySabotage(selectedSabotage.id, targetId, costOverride);
      } finally {
        setIsApplying(false);
        setSelectedSabotage(null);
        setSelectedTargetId('');
        setIsDialogOpen(false);
      }
    }
  };

  if (availableSabotages.length === 0) {
    return (
      <Card className="glass-card border-primary/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            💣 Draft Sabotages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No draft sabotages have been configured by the commissioner.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          💣 Draft Sabotages
          <Badge className="ml-auto bg-green-500/20 text-green-400 border-green-500/30 flex items-center gap-1">
            <Wallet className="w-3 h-3" />
            ${displayBalance}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {sabotagesRemaining} sabotages remaining this draft
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {availableSabotages.map((sabotage) => {
          const isKarmaSab = isKarma(sabotage);
          const effectiveCost = isKarmaSab ? getKarmaCost() : sabotage.cost;
          const canAfford = (draftCredits + walletBalance) >= effectiveCost;
          const hasRemaining = sabotagesRemaining > 0;
          const karmaDisabled = isKarmaSab && sabotageHitCount < 2;
          const isBetaDisabled = sabotage.enabled === false;
          const isPickSkip = sabotage.name === 'Pick Skip';
          const pickSkipBlocked = isPickSkip && currentRound < 10;
          const isSnakeReversal = sabotage.name === 'Snake Reversal';
          const isGlobalSab = isGlobalSabotage(sabotage);
          const snakeReversalBlocked = isSnakeReversal && currentRound < 6;
          const isDisabled = isBetaDisabled || !canAfford || !hasRemaining || karmaDisabled || pickSkipBlocked || snakeReversalBlocked;

          // Hide beta-disabled sabotages entirely
          if (isBetaDisabled) return null;

          return (
            <Dialog
              key={sabotage.id}
              open={isDialogOpen && selectedSabotage?.id === sabotage.id}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) {
                  setSelectedSabotage(null);
                  setSelectedTargetId('');
                }
              }}
            >
              <DialogTrigger asChild>
                <button
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                    colorClasses[sabotage.color],
                    isDisabled && "opacity-50 cursor-not-allowed"
                  )}
                  disabled={isDisabled}
                  onClick={() => setSelectedSabotage(sabotage)}
                >
                  <span className="text-2xl">{sabotage.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{sabotage.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {pickSkipBlocked
                        ? `Available from Round 10 (currently Round ${currentRound})`
                        : snakeReversalBlocked
                          ? `Available after Round 5 (currently Round ${currentRound})`
                          : isKarmaSab && karmaDisabled
                            ? `Need ${2 - sabotageHitCount} more targeted hit${2 - sabotageHitCount === 1 ? '' : 's'} to unlock`
                            : sabotage.effect}
                    </div>
                  </div>
                  <Badge variant="secondary" className="shrink-0">${effectiveCost}</Badge>
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="text-2xl">{sabotage.icon}</span>
                    {sabotage.name}
                  </DialogTitle>
                  <DialogDescription>
                    {sabotage.description}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="p-3 rounded-lg bg-muted/30 border flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-warning" />
                    <span className="text-sm">
                      {draftCredits >= effectiveCost
                        ? <>Credits applied, <strong>${effectiveCost}</strong> from beta credits</>
                        : <>This will cost <strong>${effectiveCost}</strong> from your wallet</>
                      }
                    </span>
                  </div>

                  {isKarmaSab ? (
                    <div className="p-3 rounded-lg bg-success/10 border border-success/30 text-sm">
                      Karma will immediately reverse the most recent active sabotage used against you and reflect it back to the sender.
                    </div>
                  ) : isGlobalSab ? (
                    <div className="p-3 rounded-lg bg-damage/10 border border-damage/30 text-sm">
                      This affects the entire draft. The pick order for the next complete round will be reversed for all teams.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Target</label>
                      <Select value={selectedTargetId} onValueChange={setSelectedTargetId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a team to sabotage" />
                        </SelectTrigger>
                        <SelectContent>
                          {otherTeams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name} ({team.owner})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setIsDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 gap-2"
                      disabled={isApplying || (!isKarmaSab && !isGlobalSab && !selectedTargetId)}
                      onClick={handleConfirmSabotage}
                    >
                      <Bomb className="w-4 h-4" />
                      {isApplying ? 'Applying...' : isKarmaSab ? 'Activate Karma' : isGlobalSab ? 'Activate' : 'Apply Sabotage'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default DraftSabotagesPanel;
